/**
 * "Waiting for approval" screen.
 *
 * Full-screen overlay shown when a new device gets NOT_PAIRED from the gateway.
 * Displays the CLI approval command, shortened device ID, countdown timer,
 * and cancel button.
 *
 * Auto-retries in the background via the PairingLoop from `src/auth/pairing.ts`.
 * When approved, the transition to connected state happens automatically
 * through the GatewayManager event system.
 */

import { useState, useCallback } from 'react';
import type { PairingState } from '../auth/pairing';
import styles from './PairingApproval.module.css';

interface PairingApprovalProps {
  /** Current pairing state (status, deviceId, countdown, etc.) */
  state: PairingState;
  /** Called when the user cancels the pairing flow */
  onCancel: () => void;
  /** Called when the user wants to retry after expiry */
  onRetry: () => void;
}

/**
 * Pairing approval overlay component.
 *
 * Shows instructions for approving the device on the gateway server,
 * with a countdown timer and auto-retry status.
 */
export function PairingApproval({ state, onCancel, onRetry }: PairingApprovalProps) {
  const [copied, setCopied] = useState(false);
  const command = 'openclaw devices approve';

  /** Shortened device ID for display (first 8 + last 4 chars) */
  const shortDeviceId = state.deviceId
    ? `${state.deviceId.slice(0, 8)}…${state.deviceId.slice(-4)}`
    : '—';

  /** Countdown display (M:SS format) */
  const remainingSecs = Math.ceil(state.remainingMs / 1000);
  const mins = Math.floor(remainingSecs / 60);
  const secs = remainingSecs % 60;
  const countdown = `${mins}:${secs.toString().padStart(2, '0')}`;

  /** Copy the approval command to clipboard */
  const copyCommand = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(command);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may not be available in all contexts */
    }
  }, [command]);

  return (
    <div className={styles.overlay}>
      <div className={styles.icon}>⏳</div>
      <h2 className={styles.title}>Waiting for Approval</h2>
      <p className={styles.description}>
        Your device needs to be approved on the gateway server.
      </p>
      <p className={styles.description}>Run this on your server:</p>

      <div className={styles.commandBox}>
        <code className={styles.command}>{command}</code>
        <button className={styles.copyBtn} onClick={copyCommand}>
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>

      <div className={styles.deviceId}>Device: {shortDeviceId}</div>

      {state.status === 'pending' && (
        <>
          <div className={styles.countdown}>Expires in {countdown}</div>
          <div className={styles.retryNote}>Auto-retrying every 10 seconds…</div>
        </>
      )}

      {state.status === 'expired' && (
        <>
          <div className={styles.expired}>Request expired</div>
          <button className={styles.retryBtn} onClick={onRetry}>
            Retry
          </button>
        </>
      )}

      <button className={styles.cancelBtn} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}
